import { useMemo, useState } from "react";
import Wrapper, { FilterPanelSelectMenuPortal } from "./VisualizeByNameFilterPanel.styles";
import VisualizeByNameFilter from "./VisualizeByNameFilter";
import { numberToWords } from "../../../Utility/SharedUtility";
import { isLiveDashboardChart } from "./liveDashboardDefaults";
import { isAssetClassDimension } from "./liveDashboardAssetClass";
import { isClientSegmentDimension } from "./liveDashboardDefaults";
import { isGeographyDimension } from "./liveDashboardGeography";
import { isCurrencyDimension } from "./liveDashboardCurrency";
import Outline32IRemove from '@mds/mds-icons/icons/svg/outline-32-i-remove.svg';
import { Button, Icon, PRIMARY_BUTTON, SECONDARY_BUTTON, SIZE_SMALL, Toggle, TYPE_OUTLINE } from "@mds/mds-reactjs-library";

export const VisualizeByNameFilterPanel = ({ setFilterPanelIsOpen, openAccordionDimensionId, setOpenAccordionDimensionId, selections, setSelection, filterPanelApplyClicked, dimensions, chart, chartConfiguration, headMessage }) => {

    const [showDefinition, setShowDefinition] = useState(false)
    const [validationMessage, setValidationMessage] = useState('')
    const getDimensionConfiguration = (dimensionName) => chartConfiguration?.Dimensions?.[dimensionName] || {};
    const availableDimensions = useMemo(
        () => (dimensions || []).filter(dimension => !getDimensionConfiguration(dimension.Name)["SelectAllAndHide"]),
        [dimensions, chartConfiguration]
    );
    const selectionByDimensionId = useMemo(
        () => new Map((selections || []).map(selection => [selection.DimensionID, selection])),
        [selections]
    );

    const filterInstructionText = useMemo(() => {
        const count = availableDimensions.length;
        const countWord = numberToWords(count);
        if (!count) {
            return "Select filters, then click 'Apply'.";
        }
        const currentIndex = availableDimensions.findIndex(
            (d) => d.DimensionID === openAccordionDimensionId
        );
        const currentDimension =
            currentIndex >= 0 ? availableDimensions[currentIndex] : null;

        if (!currentDimension) {
            return `Select filters from up to ${countWord} categories, then click 'Apply'.`;
        }

        const display = (currentDimension.DisplayName || '').trim();
        const displayKey = display.toLowerCase();
        const dimensionName = (currentDimension.Name || '').trim();
        const chartName = (chart?.title || chart?.name || '').trim().toLowerCase();

        if (
            isLiveDashboardChart(chart) &&
            isGeographyDimension(currentDimension)
        ) {
            return 'Select one or more regions/countries.';
        }
        if (
            isLiveDashboardChart(chart) &&
            isAssetClassDimension(currentDimension)
        ) {
            return 'Select one or more asset classes.';
        }
        if (
            isLiveDashboardChart(chart) &&
            isClientSegmentDimension(currentDimension)
        ) {
            return 'Select one or more client segments.';
        }
        if (
            isLiveDashboardChart(chart) &&
            isCurrencyDimension(currentDimension)
        ) {
            return 'Select currency and click \'Apply\'.';
        }
        if (
            chartName === 'net flows by investor type and asset class' || chartName === 'aum by client segment and asset class' || chartName === 'revenue by client segment and asset class' &&
            (displayKey === 'geographies' || dimensionName === 'CountryID')
        ) {
            return `Select up to ${numberToWords(1)} geography`;
        }
        if (
            chartName === 'net flows by geography and asset class' || chartName === 'aum by geography and asset class' || chartName === 'revenue by geography and asset class' &&
            (displayKey === 'segmentid' || dimensionName === 'SegmentID')
        ) {
            return `Select up to ${numberToWords(1)} client segment`;
        }
        if (
            displayKey === 'asset classes' ||
            displayKey === 'asset class' ||
            displayKey === 'client segments' ||
            displayKey === 'client segment' ||
            displayKey === 'vehicle' ||
            displayKey === 'vehicles'
        ) {
            return '';
        }
        if (displayKey === 'years') {
            if (isLiveDashboardChart(chart)) {
                return `Choose a start year and an end year using the two dropdowns.`;
            } else {
                return `Choose a start year and an end year using the two dropdowns, then click 'Apply'.`;
            }
        }

        const position = currentIndex + 1;
        return `Select filters for ${display}.`;
    }, [availableDimensions, openAccordionDimensionId, chart]);

    const nextClicked = () => {
        if (!availableDimensions.length) {
            return;
        }

        const currentDimensionIndex = availableDimensions.findIndex(dimension => dimension.DimensionID === openAccordionDimensionId);
        const fallbackIndex = currentDimensionIndex < 0 ? 0 : currentDimensionIndex;
        const nextDimensionIndex = fallbackIndex === availableDimensions.length - 1 ? 0 : fallbackIndex + 1;

        const activeSelection = openAccordionDimensionId > 0 ? selectionByDimensionId.get(openAccordionDimensionId) : null;
        const isValid = openAccordionDimensionId > 0 ? activeSelection?.IsValid : true
        if (!isValid) {
            const dimensionDetail = dimensions.find(val => val.DimensionID === openAccordionDimensionId);
            const dimensionConfiguration = getDimensionConfiguration(dimensionDetail?.Name);

            if (!isNaN(dimensionConfiguration.MinSelections) && dimensionConfiguration.MinSelections) {
                setValidationMessage(`Please select at least ${numberToWords(dimensionConfiguration.MinSelections)} ${dimensionConfiguration.MinSelections === 1 ? 'item' : 'items'} for ${dimensionDetail.DisplayName}`);
            }
            return
        }
        setValidationMessage('');
        setOpenAccordionDimensionId(availableDimensions[nextDimensionIndex].DimensionID);
    }

    return (
        <Wrapper>
            <FilterPanelSelectMenuPortal />
            <Icon className="filter-panel-close" type={TYPE_OUTLINE} src={Outline32IRemove} onClick={e => setFilterPanelIsOpen(false)} />
            <div className="head-part">
                {/* <h1>Filters</h1> */}
                {filterInstructionText ? (
                    <>
                        <p>{filterInstructionText}</p>
                        <br />
                    </>
                ) : null}
                {/* <Toggle size={SIZE_SMALL} label="Definitions" onChange={e => setShowDefinition(e.target.checked)} /> */}
            </div>
            <div className="items">
                {
                    selections.length > 0 &&
                    availableDimensions
                        .map(dimension =>
                            <VisualizeByNameFilter
                                key={dimension.DimensionID}
                                showDefinition={showDefinition}
                                openAccordionDimensionId={openAccordionDimensionId}
                                setOpenAccordionDimensionId={setOpenAccordionDimensionId}
                                dimension={dimension}
                                setSelection={setSelection}
                                selection={selectionByDimensionId.get(dimension.DimensionID)}
                                chart={chart}
                                chartConfiguration={chartConfiguration}
                                setValidationMessage={setValidationMessage}
                                headMessage={dimension.DisplayName === "Years" ? headMessage : ""}
                            />
                        )
                }
            </div>
            <div className="foot-part" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'left', overflow: 'visible' }}>
                <div style={{ minHeight: '24px', width: '100%' }}>
                    {openAccordionDimensionId &&
                        dimensions.find(d => d.DimensionID === openAccordionDimensionId)?.DisplayName === "Years" &&
                        headMessage && (
                            <span style={{ color: '#000', fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                                {headMessage}
                            </span>
                        )
                    }
                    <span style={{ fontSize: '14px' }} className="ValidationMsg">{validationMessage}</span>
                </div>
                <div className="foot-part-buttons">
                    <Button appearance={SECONDARY_BUTTON} onClick={nextClicked}>Next</Button>
                    <Button appearance={PRIMARY_BUTTON} disabled={selections?.length > 0 && selections?.some(selection => !selection.IsValid)} onClick={filterPanelApplyClicked}>Apply</Button>
                </div>
            </div>
        </Wrapper>
    )
}
